/**
 * MappingStep — etapa 2 de "Importar base de clientes" (Item 391-C).
 *
 * Para cada campo de negócio (Telefone* obrigatório, Nome do titular, CPF/CNPJ,
 * Nº do contrato, Papel) um <select> com as colunas do CSV. Telefone obrigatório
 * → o caller desabilita o botão Importar quando vazio. Inclui o seletor de canal
 * padrão das identities criadas.
 */

import type { ImportBaseMapping, ImportDefaultChannel } from '../../lib/types';

export interface MappingDraft {
  phone: string;
  holderName: string;
  cpfCnpj: string;
  contractNumber: string;
  role: string;
}

interface FieldDef {
  key: keyof MappingDraft;
  label: string;
  required: boolean;
}

const FIELDS: FieldDef[] = [
  { key: 'phone', label: 'Telefone', required: true },
  { key: 'holderName', label: 'Nome do titular', required: false },
  { key: 'cpfCnpj', label: 'CPF / CNPJ', required: false },
  { key: 'contractNumber', label: 'Nº do contrato', required: false },
  { key: 'role', label: 'Papel', required: false },
];

export interface MappingStepProps {
  headers: string[];
  draft: MappingDraft;
  defaultChannel: ImportDefaultChannel;
  onChange: (draft: MappingDraft) => void;
  onChannelChange: (channel: ImportDefaultChannel) => void;
}

/** Converte o rascunho (strings vazias = não mapeado) no `mapping` do wire. */
export function buildMapping(draft: MappingDraft): ImportBaseMapping {
  const mapping: ImportBaseMapping = { phone: draft.phone };
  if (draft.holderName !== '') mapping.holderName = draft.holderName;
  if (draft.cpfCnpj !== '') mapping.cpfCnpj = draft.cpfCnpj;
  if (draft.contractNumber !== '') mapping.contractNumber = draft.contractNumber;
  if (draft.role !== '') mapping.role = draft.role;
  return mapping;
}

export function MappingStep({
  headers,
  draft,
  defaultChannel,
  onChange,
  onChannelChange,
}: MappingStepProps): JSX.Element {
  return (
    <section className="cr-step" aria-labelledby="cr-map-title">
      <h2 id="cr-map-title" className="cr-step__title">
        <span className="cr-step__num">2</span>
        Mapear colunas
      </h2>

      <div className="cr-map__grid">
        {FIELDS.map((field) => {
          const selectId = `cr-map-${field.key}`;
          return (
            <div key={field.key} className="cr-map__row">
              <label className="cr-map__label" htmlFor={selectId}>
                {field.label}
                {field.required && (
                  <span className="cr-map__req" aria-hidden>
                    *
                  </span>
                )}
              </label>
              <select
                id={selectId}
                className="cr-map__select"
                value={draft[field.key]}
                onChange={(e) => onChange({ ...draft, [field.key]: e.target.value })}
                data-testid={`cr-map-select-${field.key}`}
              >
                <option value="">
                  {field.required ? '— selecione a coluna —' : '— não importar —'}
                </option>
                {headers.map((header) => (
                  <option key={header} value={header}>
                    {header}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>

      <div className="cr-map__channel">
        <label className="cr-map__label" htmlFor="cr-map-channel">
          Canal padrão das identidades criadas
        </label>
        <select
          id="cr-map-channel"
          className="cr-map__select"
          value={defaultChannel}
          onChange={(e) => onChannelChange(e.target.value as ImportDefaultChannel)}
          data-testid="cr-map-channel"
        >
          <option value="whatsapp_cloud_api">WhatsApp</option>
          <option value="telegram">Telegram</option>
        </select>
      </div>
    </section>
  );
}
